import { Injectable } from '@angular/core';
import { IFilterListService } from './ifilter-list.service';
import { map } from 'rxjs';
import { SharedQueries } from 'src/app/shared/types/get-query';
import { Gender } from 'src/app/components/company/models/new/style';
import { GenderMapper } from 'src/app/components/company/services/gender-mapper';
import { Selectable } from 'src/app/shared/types/selectable.type';

@Injectable({
	providedIn: 'root'
})
export class GenderFilterService extends IFilterListService<Gender> {
	private readonly PAGE_SIZE = 50;

	private currentSearch = '';
	private currentPage = 0;

	private hasNextPage = true;

	private internalDatasource: Selectable<Gender>[] = [];

	constructor(private genderMapper: GenderMapper) {
		super();
	}

	pageCursorCalculator(pageIndex: number, pageSize: number): string {
		return btoa((pageSize * pageIndex - 1).toString());
	}

	reset(): void {
		this.currentPage = 0;
		this.hasNextPage = true;
		this.internalDatasource = [];
		this.datasource.next([]);
	}

	search(searchText: string, variables?: any, append: boolean = false): void {
		if (this.loading) return;
		if (append && !this.hasNextPage) return;

		if (!append) {
			this.reset();
			this.currentSearch = searchText;
		}

		const after = this.currentPage === 0 ? null : this.pageCursorCalculator(this.currentPage, this.PAGE_SIZE);

		this.loading = true;
		this.graphQlService
			.getData<any>(SharedQueries.GET_GENDERS, {
				where: {
					name: {
						contains: this.currentSearch
					}
				},
				first: this.PAGE_SIZE,
				after,
				...variables
			})
			.pipe(
				map((res: any) => {
					return res.data.genders.nodes.map((node: any) => ({
						...this.genderMapper.toGender(node),
						displayId: node.id,
						displayName: node.name,
						selected: false,
						disabled: false
					}));
				})
			)
			.subscribe({
				next: (items: Selectable<Gender>[]) => {
					this.loading = false;
					if (!items.length) {
						this.hasNextPage = false;
						return;
					}

					const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));

					this.internalDatasource = [...this.internalDatasource, ...unique];

					this.datasource.next(this.internalDatasource);

					this.currentPage++;

					if (items.length < this.PAGE_SIZE) {
						this.hasNextPage = false;
					}
				},
				error: () => {
					this.loading = false;
				}
			});
	}

	loadNextPage(): void {
		this.search(this.currentSearch, {}, true);
	}

	preloadUntilSelected(selected: Array<Selectable<Gender>>, searchText: string = ''): void {
		if (!selected?.length) {
			this.search(searchText);
			return;
		}

		this.reset();

		const targetIds = selected.map((el) => el.displayId);

		const recursiveLoad = () => {
			if (this.loading || !this.hasNextPage) return;

			const after = this.currentPage === 0 ? null : this.pageCursorCalculator(this.currentPage, this.PAGE_SIZE);

			this.loading = true;

			this.graphQlService
				.getData<any>(SharedQueries.GET_GENDERS, {
					where: {
						name: {
							contains: searchText
						}
					},
					first: this.PAGE_SIZE,
					after
				})
				.pipe(
					map((res: any) => {
						return res.data.genders.nodes.map((node: any) => ({
							...this.genderMapper.toGender(node),
							displayId: node.id,
							displayName: node.name,
							selected: false,
							disabled: false
						}));
					})
				)
				.subscribe({
					next: (items: Selectable<Gender>[]) => {
						if (!items.length) {
							this.hasNextPage = false;
							this.loading = false;
							return;
						}

						const unique = items.filter((item) => !this.internalDatasource.some((el) => el.displayId === item.displayId));

						this.internalDatasource = [...this.internalDatasource, ...unique];

						this.datasource.next(this.internalDatasource);

						this.currentPage++;

						const foundAll = targetIds.every((id) => this.internalDatasource.some((el) => el.displayId === id));

						if (foundAll) {
							this.loading = false;
							return;
						}

						if (items.length < this.PAGE_SIZE) {
							this.hasNextPage = false;
							this.loading = false;
							return;
						}

						this.loading = false;

						recursiveLoad();
					},
					error: () => {
						this.loading = false;
					}
				});
		};

		recursiveLoad();
	}

	searchById(_id: string[] | string) {
		const ids = Array.isArray(_id) ? _id.map((id) => Number(id)) : [Number(_id)];

		return this.graphQlService
			.getData<any>(SharedQueries.GET_GENDERS, {
				where: {
					id: {
						in: ids
					}
				},
				first: ids.length
			})
			.pipe(map((res) => res.data.genders.nodes.map((el: any) => this.genderMapper.toGender(el))));
	}
}
